import {
  Button,
  Flex,
  Modal,
  MultiSelect,
  ScrollArea,
  Select,
  TextInput,
} from "@mantine/core";
import { SelectItem } from "@mantine/core/lib/Select/types";
import { isNotEmpty, useForm } from "@mantine/form";
import { sortBy } from "lodash";
import { useCallback } from "react";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useInvoices } from "../../api/hooks/invoice/useInvoices.ts";
import SvgUnfold from "../../components/Icons/Unfold.tsx";
import Preloader from "../../components/Preloader/Preloader.tsx";
import { CreateDeskCollectionInput } from "../../graphql/generated.ts";
import { getCompanyOptions } from "../../utils/company.ts";

interface Props {
  opened: boolean;
  templates: SelectItem[];
  onClose: () => void;
  onSubmit: (value: CreateDeskCollectionInput) => void;
}

export const DeskCollectionModal = ({
  templates,
  opened,
  onClose,
  onSubmit,
}: Props) => {
  const form = useForm<{
    customerId: string;
    picName: string;
    phone: string;
    invoices: string[];
    templateId: string;
  }>({
    validate: {
      customerId: isNotEmpty("Field is required"),
      picName: isNotEmpty("Field is required"),
      phone: isNotEmpty("Field is required"),
      invoices: isNotEmpty("Field is required"),
      templateId: isNotEmpty("Field is required"),
    },
    validateInputOnBlur: true,
  });

  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data;

  const [{ data: invoicesResponse, fetching: fetchingInvoices }] = useInvoices({
    filter: { customerId: { equals: Number(form.values.customerId) } },
    pause: !form.values.customerId,
  });
  const invoices = invoicesResponse?.invoices.data;
  const invoicesOptions = sortBy(
    invoices?.map((invoice) => ({
      value: String(invoice.id),
      label: invoice.invoiceNumber,
    })) || [],
    "label"
  );

  const fetching = fetchingCustomers || fetchingInvoices;

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }
    onSubmit({
      ...form.values,
      customerId: Number(form.values.customerId),
      templateId: Number(form.values.templateId),
      invoices: form.values.invoices.map(Number),
    });
    handleClose();
  }, [form, handleClose, onSubmit]);

  return (
    <>
      <Modal
        opened={opened}
        title="Create Desk Collection Record"
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="md"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        {fetching && <Preloader loading={true} />}
        <Flex direction="column" gap={20}>
          <Flex gap={12} direction="column">
            <Select
              size="m"
              required
              searchable
              clearable
              label="Customer"
              placeholder="Select Customer"
              data={getCompanyOptions(customers)}
              {...form.getInputProps("customerId")}
            />
            <TextInput
              size="m"
              required
              label="PIC Name"
              placeholder="Enter PIC Name"
              {...form.getInputProps("picName")}
            />
            <TextInput
              size="m"
              required
              type="phone"
              label="Phone Number"
              placeholder="Enter Phone Number"
              {...form.getInputProps("phone")}
            />
            <MultiSelect
              size="m"
              required
              searchable
              clearable
              multiple
              disabled={!form.values.customerId || fetchingInvoices}
              label="Invoices"
              placeholder="Select Invoices"
              data={invoicesOptions}
              rightSection={<SvgUnfold />}
              {...form.getInputProps("invoices")}
            />
            <Select
              size="m"
              required
              searchable
              clearable
              label="Template"
              placeholder="Select Template"
              data={templates}
              {...form.getInputProps("templateId")}
            />
          </Flex>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid()}
              loading={fetchingCustomers || fetchingInvoices}
              onClick={handleSubmit}
            >
              save
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
